/**
 * Inbox/notifications command handlers
 */

import { createInboxService } from '../utils/client.js';
import { chalk, formatOutput, formatSuccess } from '../utils/format.js';
import { handleApiError } from '../utils/errors.js';
import type { InboxNotification } from '../../../../src/lib/services/inbox.service.js';

type InboxNotificationWithMeta = InboxNotification & { _createdAt: Date };

type DateBucket = string; // 'TODAY' | 'YESTERDAY' | 'LAST_WEEK' | 'MAR 2026' | 'FEB 2026' etc.

function getDateBucket(date: Date): DateBucket {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d = new Date(date);
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today.getTime() - dayStart.getTime()) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return 'TODAY';
  if (diffDays === 1) return 'YESTERDAY';
  if (diffDays <= 7) return 'LAST WEEK';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); // "Mar 2026"
}

function formatDateForBucket(date: Date, bucket: DateBucket): string {
  const d = new Date(date);
  if (bucket === 'TODAY') {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  if (bucket === 'YESTERDAY' || bucket === 'LAST WEEK') {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getBucketSortKey(bucket: DateBucket): number {
  if (bucket === 'TODAY') return 0;
  if (bucket === 'YESTERDAY') return 1;
  if (bucket === 'LAST WEEK') return 2;
  return 3; // Month buckets - keep chronological
}

function getTypeLabel(type: string): string {
  const map: Record<string, string> = {
    Comment: 'Comment',
    TaskMovedToInbox: 'TaskMoved',
    TaskMoved: 'TaskMoved',
    Assignee: 'Assigned',
    Reaction: 'Reacted',
    TaskArchived: 'TaskArchived',
    Mentioned: 'Mentioned',
    TaskOverdue: 'TaskOverdue',
    AddedToFollowerInTask: 'AddedToFollower',
    Invite: 'Invite',
  };
  return map[type] || type;
}

function getTypeColor(type: string): (s: string) => string {
  const colors: Record<string, (s: string) => string> = {
    Comment: chalk.cyan,
    TaskMovedToInbox: chalk.blue,
    TaskMoved: chalk.blue,
    Assignee: chalk.green,
    Reaction: chalk.magenta,
    TaskArchived: chalk.yellow,
    Mentioned: chalk.cyan,
    TaskOverdue: chalk.red,
    AddedToFollowerInTask: chalk.gray,
  };
  return colors[type] || chalk.white;
}

function formatLabels(taskLabels: Array<{ name?: string }> | undefined): string {
  if (!taskLabels?.length) return '';
  const names = taskLabels.map((l) => l.name).filter(Boolean).slice(0, 3);
  if (names.length === 0) return '';
  return names.map((n) => chalk.dim(`[${n}]`)).join(' ') + ' ';
}

function printGroupedInboxNotifications(
  sectionTitle: string,
  notifications: InboxNotification[],
  formatSender: (n: InboxNotification) => string
): void {
  if (notifications.length === 0) return;

  console.log('');
  console.log(chalk.bold.white(`  ${sectionTitle}`));
  console.log(chalk.dim('  ' + '─'.repeat(40)));
  const grouped = new Map<DateBucket, InboxNotificationWithMeta[]>();
  for (const n of notifications) {
    const createdAt = n.createdAt ? new Date(n.createdAt) : new Date();
    const bucket = getDateBucket(createdAt);
    if (!grouped.has(bucket)) grouped.set(bucket, []);
    grouped.get(bucket)!.push({ ...n, _createdAt: createdAt });
  }

  const sortedBuckets = Array.from(grouped.keys()).sort((a, b) => {
    const ka = getBucketSortKey(a);
    const kb = getBucketSortKey(b);
    if (ka !== kb) return ka - kb;
    if (ka === 3) {
      const da = grouped.get(a)![0]._createdAt;
      const db = grouped.get(b)![0]._createdAt;
      return db.getTime() - da.getTime();
    }
    return 0;
  });

  for (const bucket of sortedBuckets) {
    const items = grouped.get(bucket)!;
    if (!items?.length) continue;

    console.log(chalk.blue.bold(`  ${bucket}`));
    for (const n of items) {
      const task = n.task;
      const sender = formatSender(n);
      const typeLabel = getTypeLabel(n.type);
      const typeColor = getTypeColor(n.type);
      const title = (task?.title || 'Task').replace(/\s+/g, ' ');
      const ticket = task?.ticketNumber || '';
      const labels = formatLabels(task?.taskLabels as Array<{ name?: string }> | undefined);
      const timeStr = formatDateForBucket(n._createdAt, bucket);
      const unreadDot = n.seen === false ? chalk.cyan('●') : chalk.dim('○');

      const titleShort = title.length > 55 ? title.slice(0, 52) + '…' : title;
      const ticketPart = ticket ? chalk.bold.cyan(ticket) + ' ' : '';
      const desc = chalk.white(titleShort);

      const line = [unreadDot, sender, typeColor(typeLabel + ':'), labels, ticketPart, desc]
        .filter(Boolean)
        .join(' ');

      console.log(`  ${line}  ${chalk.dim(timeStr.padStart(8))}`);
    }
    console.log('');
  }
}

export async function listInbox(
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createInboxService(token, apiUrl);
    // HTPR-3079: Omit user_id — backend resolves inbox from Authorization JWT (same as web).
    // Decoding JWT locally often mismatched mcp_token claims and returned empty tabs.
    const result = await service.listInbox({});

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      const userList = (result.user_notifications ?? result.items ?? []) as InboxNotification[];
      const agentList = (result.agent_notifications ?? []) as InboxNotification[];

      if (userList.length === 0 && agentList.length === 0) {
        console.log(chalk.dim('\n  No notifications'));
        return;
      }

      printGroupedInboxNotifications('User notifications', userList, (n) =>
        chalk.dim(n.fromUser?.displayName || 'Unknown')
      );
      printGroupedInboxNotifications('Agent notifications', agentList, (n) =>
        chalk.dim(n.fromAgent?.displayName || n.fromUser?.displayName || 'Unknown')
      );
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function archiveInbox(
  notificationIds: number[],
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    if (!notificationIds || notificationIds.length === 0) {
      throw new Error('At least one notification ID required');
    }

    const service = await createInboxService(token, apiUrl);
    const result = await service.archiveInbox({ notification_ids: notificationIds });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      const count = notificationIds.length;
      console.log('');
      console.log(`  ${chalk.green.bold('✓ Archived')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      console.log(`  ${chalk.dim('Count')}   ${chalk.white(String(count))} ${chalk.dim(count === 1 ? 'notification' : 'notifications')}`);
      console.log(`  ${chalk.dim('IDs')}     ${notificationIds.map((id) => chalk.cyan(String(id))).join(chalk.dim(', '))}`);
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function unarchiveInbox(
  notificationIds: number[],
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    if (!notificationIds || notificationIds.length === 0) {
      throw new Error('At least one notification ID required');
    }

    const service = await createInboxService(token, apiUrl);
    const result = await service.unarchiveInbox({ notification_ids: notificationIds });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Unarchived ${notificationIds.length} notification(s)`));
    }
  } catch (error) {
    handleApiError(error);
  }
}
